/**
 * Updates (operation changelog) dual-write helper.
 *
 * Updates are append-only audit records (deleteProduct, deleteSale,
 * deleteWorkingHour, deleteComplexSale, …) written to Firestore
 * `organizations/{orgId}/updates`. This mirrors that write to Supabase via the
 * backend so the Supabase `updates` table stays current and the Updates screen
 * can read from Supabase.
 *
 * Best-effort by design: the parent operation (and its own Supabase dual-write)
 * has already committed by the time this runs, so an audit-log failure must not
 * surface as an error to the user — it is logged + reported instead.
 */
import { bestEffortDualWrite } from './dualWrite'
import { updatesApi } from '../API/typedApiClient'

interface UpdateLogInput {
  /**
   * ATL-911 — REQUIRED, and that is the fix.
   *
   * Every call site created its Firestore doc with an anonymous ref
   * (`batch.set(db.collection('updates').doc(), …)`) and never captured the generated id, so this
   * helper POSTed without one and the Supabase row landed with `firestore_id IS NULL`. The
   * set-diff matches on `firestore_id`, so the row is invisible to it and the FIRESTORE document
   * reads as MISSING — which is where "150 `updates` documents missing" came from. The rows are
   * present; the number was arithmetic on an exclusion. Worse, a missing document invites a
   * backfill, and that backfill has already written 1,487 duplicate rows across 45 orgs.
   *
   * It is required rather than optional deliberately: the backend DTO has accepted `firestoreId`
   * all along, and an optional field on this side is exactly how it went unnoticed for the whole
   * life of the feature. A missing id must now fail the build, not the reconciliation.
   */
  firestoreId: string
  type: string
  cashierUID?: string
  cashierUid?: string
  date?: Date | string
  dataBefore?: unknown
  dataAfter?: unknown
}

export const dualWriteUpdate = async (update: UpdateLogInput): Promise<void> => {
  // Dual-writes must happen regardless of the READ mode (shouldUseSupabase()). Gating this on
  // the read toggle would stop mirroring the changelog to Supabase whenever reads are on
  // Firestore (the launch default), letting Supabase drift.
  //
  // Routed through bestEffortDualWrite (ATL-786 round 2) so the changelog mirror gets the FULL
  // dual-write discipline instead of a private lesser copy: the same canary/kill-switch gate,
  // the stale-policy refetch + rc-stale escalation + in-session replay (this path used to skip
  // entire stale-cohort sessions with zero signal — 'updates' is the second-largest census
  // gap), structured failure reporting, and durable outbox capture on real failures.
  await bestEffortDualWrite('updates.create', () => {
    const date =
      update.date instanceof Date
        ? update.date.toISOString()
        : (update.date ?? new Date().toISOString())
    return updatesApi.createUpdate({
      // ATL-911: the id of the Firestore document this row mirrors. Without it the row is
      // unaddressable and its Firestore twin reads as missing forever.
      firestoreId: update.firestoreId,
      type: update.type,
      cashierUid: update.cashierUid || update.cashierUID,
      date,
      dataBefore: update.dataBefore,
      dataAfter: update.dataAfter,
    } as Parameters<typeof updatesApi.createUpdate>[0])
  },
  // Carrying the document identity as dual-write context too, so the outbox can supersede and
  // purge by document like every other write path.
  { firestoreId: update.firestoreId })
}

/**
 * ATL-1445 stage 3 P9 — one record of the Updates feed, written the way every W writer's batch writes
 * it (an `updates` document under the organization, its id minted first and carried in the record,
 * ATL-911), then mirrored by `dualWriteUpdate`.
 *
 * For a converted W writer in SUPABASE mode, whose Firestore batch does not run: the feed is outside W
 * (`updates` is not fenced; stage-3 §1 "Writers outside W … updates … keep bestEffortDualWrite and the
 * v1 outbox, even for a cut-over org"), so its record stays Firestore-first while the books write went
 * to the backend. Registered as an out-of-W writer (`wWriterRegistry.ts`), which is what lets a
 * converted writer call it outside `firestoreFirst`. Firestore mode never calls it: the batch writes the
 * record.
 */
export const saveUpdateRecord = async (
  db: { collection: (name: 'updates') => { doc: () => { id: string; set: (data: UpdateLogInput) => Promise<unknown> } } },
  record: Omit<UpdateLogInput, 'firestoreId'>,
): Promise<void> => {
  const updateRef = db.collection('updates').doc()
  const update: UpdateLogInput = { firestoreId: updateRef.id, ...record }
  await updateRef.set(update)
  await dualWriteUpdate(update)
}
